import React, { useState } from "react";
import { Play, X } from "lucide-react";
import { useSettings } from "../../lib/useSettings";

/**
 * VideoIntroSection — Section de présentation vidéo (Phase 31).
 * Activée via settings.video_intro_enabled.
 *
 * Sources supportées (priorité) :
 *   1. video_intro_youtube_id  → embed YouTube (ID seul, pas l'URL complète)
 *   2. video_intro_self_hosted_url → tag <video> avec poster
 *
 * Tant que l'utilisateur n'a pas cliqué Play, on n'embarque rien (perf + RGPD).
 */
export default function VideoIntroSection() {
  const { settings: s } = useSettings();
  const [open, setOpen] = useState(false);

  if (!s?.video_intro_enabled) return null;

  const ytId = (s.video_intro_youtube_id || "").trim();
  const mp4 = (s.video_intro_self_hosted_url || "").trim();
  if (!ytId && !mp4) return null;

  const poster = s.video_intro_poster_url
    || (ytId ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg` : "");

  return (
    <section className="py-20 md:py-28 bg-[hsl(var(--background))]" data-testid="video-intro-section">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          {s.video_intro_kicker && (
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-3" data-testid="video-intro-kicker">{s.video_intro_kicker}</div>
          )}
          <h2 className="font-display text-3xl md:text-5xl font-bold text-[color:var(--brand-navy)] leading-tight max-w-3xl mx-auto" data-testid="video-intro-title">
            {s.video_intro_title || "Présentation"}
          </h2>
          {s.video_intro_subtitle && (
            <p className="mt-4 text-base text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto" data-testid="video-intro-subtitle">{s.video_intro_subtitle}</p>
          )}
        </div>

        <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl bg-[color:var(--brand-navy)] group">
          {!open ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="absolute inset-0 w-full h-full"
              aria-label="Lire la vidéo"
              data-testid="video-intro-play"
            >
              {poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[color:var(--brand-red)] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform ring-4 ring-white/20">
                  <Play className="w-9 h-9 md:w-11 md:h-11 text-white ml-1.5" fill="currentColor" />
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white text-center md:text-left">
                <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">Cliquez pour lire</div>
                <div className="text-sm md:text-base font-semibold mt-1 opacity-95">{s.video_intro_title}</div>
              </div>
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur flex items-center justify-center text-white"
                aria-label="Fermer la vidéo"
                data-testid="video-intro-close"
              >
                <X className="w-4 h-4" />
              </button>
              {ytId ? (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`}
                  title={s.video_intro_title || "Vidéo de présentation"}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  data-testid="video-intro-iframe"
                />
              ) : (
                <video
                  src={mp4}
                  poster={poster}
                  controls
                  autoPlay
                  className="w-full h-full object-cover"
                  data-testid="video-intro-mp4"
                />
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
